import React, { useState, useEffect } from 'react'; // Added useState & useEffect
import { Box, Container, Typography, styled, Stack } from '@mui/material';
import { motion, useScroll, useSpring } from 'framer-motion';
import { db } from '../../firebaseConfig'; // Adjust path if needed
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// --- STYLED COMPONENTS (No Changes Made Here) ---

const JourneyWrapper = styled(Box)({
    padding: '120px 0',
    background: 'radial-gradient(circle at 50% -20%, #1e293b 0%, #000 80%)',
    backgroundColor: '#000',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
});

const GlowOrb = styled(motion.div)({
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
});

const TimelineLineContainer = styled(Box)({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '4px',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
    zIndex: 1,
    overflow: 'hidden',
    '@media (max-width: 900px)': { left: '20px' }
});

const TimelineProgress = styled(motion.div)({
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, transparent, #3b82f6, #60a5fa, transparent)',
    transformOrigin: 'top',
});

const CardContainer = styled(motion.div)({
    width: '90%',
    maxWidth: '520px',
    position: 'relative',
    borderRadius: '28px',
    padding: '1.5px',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(255, 255, 255, 0.05) 100%)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
});

const InnerCard = styled(Box)({
    background: 'rgba(15, 23, 42, 0.8)',
    backdropFilter: 'blur(30px)',
    borderRadius: '27px',
    padding: '35px',
    position: 'relative',
    zIndex: 5,
    border: '1px solid rgba(255, 255, 255, 0.08)',
});

const StarStreak = styled(motion.span)(({ vertical }) => ({
    position: 'absolute',
    display: 'block',
    background: vertical 
        ? 'linear-gradient(180deg, transparent, #3b82f6, #fff, transparent)' 
        : 'linear-gradient(90deg, transparent, #3b82f6, #fff, transparent)',
    zIndex: 10,
    width: vertical ? '2px' : '180px',
    height: vertical ? '180px' : '2px',
    filter: 'blur(1.5px)',
}));

const TimelineDot = styled(motion.div)({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '20px',
    backgroundColor: '#000',
    border: '3px solid #3b82f6',
    borderRadius: '50%',
    boxShadow: '0 0 15px #3b82f6',
    zIndex: 10,
    marginTop: '35px',
    '@media (max-width: 900px)': { left: '20px' }
});

// --- COMPONENT ---

const CareerJourney = () => {
    const { scrollYProgress } = useScroll();
    const scaleY = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
    
    // --- Dynamic Data Fetching Logic ---
    const [journeyData, setJourneyData] = useState([]);

useEffect(() => {
    const fetchData = async () => {
        try {
            const q = query(collection(db, "careerJourney")); // OrderBy yahan se hata diya
            const querySnapshot = await getDocs(q);
            
            const rawData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // --- Custom Sorting Logic ---
            const sortedData = rawData.sort((a, b) => {
                // Hum "MM/YYYY - ..." format se sirf start date nikaal rahe hain
                const dateA = a.year.split(' - ')[0]; 
                const dateB = b.year.split(' - ')[0];

                const [monthA, yearA] = dateA.split('/').map(Number);
                const [monthB, yearB] = dateB.split('/').map(Number);

                // Pehle Year compare karo
                if (yearA !== yearB) {
                    return yearB - yearA; // Latest year pehle
                }
                // Agar year same hai, toh Month compare karo
                return monthB - monthA; // Latest month pehle
            });

            setJourneyData(sortedData);
        } catch (error) {
            console.error("Error fetching career journey: ", error);
        }
    };
    fetchData();
}, []);

    const starTransition = { duration: 2.5, repeat: Infinity, ease: "linear" };

    return (
        <JourneyWrapper>
            {/* Background Orbs */}
            <GlowOrb 
                animate={{ x: [0, 100, 0], y: [0, 50, 0] }} 
                transition={{ duration: 10, repeat: Infinity }}
                sx={{ width: '400px', height: '400px', background: 'rgba(59, 130, 246, 0.05)', top: '10%', left: '5%' }} 
            />
            <GlowOrb 
                animate={{ x: [0, -80, 0], y: [0, 120, 0] }} 
                transition={{ duration: 15, repeat: Infinity }}
                sx={{ width: '300px', height: '300px', background: 'rgba(96, 165, 250, 0.05)', bottom: '10%', right: '5%' }} 
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', mb: 15 }}>
                    <Typography variant="overline" sx={{ color: '#3b82f6', fontWeight: 900, letterSpacing: 6, display: 'block', mb: 2 }}>
                        PATH TO MASTERY
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '4.5rem' }, background: 'linear-gradient(to bottom, #fff 40%, #64748b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Career <span style={{ color: '#3b82f6' }}>Milestones</span>
                    </Typography>
                </Box>

                <Box sx={{ position: 'relative' }}>
                    <TimelineLineContainer>
                        <TimelineProgress style={{ scaleY }} />
                    </TimelineLineContainer>
                    
                    {journeyData.map((item, index) => {
                        // Data cleanup logic: check if skills is string or array
                        const skillsArray = Array.isArray(item.skills) 
                            ? item.skills 
                            : (typeof item.skills === 'string' ? item.skills.split(',').map(s => s.trim()) : []);

                        return (
                            <Box key={item.id || index} sx={{ 
                                display: 'flex', 
                                justifyContent: item.isLeft ? 'flex-end' : 'flex-start',
                                mb: 12, width: '100%', position: 'relative',
                                '@media (max-width: 900px)': { justifyContent: 'flex-start', pl: '50px' }
                            }}>
                                <TimelineDot whileInView={{ scale: [0, 1.2, 1], backgroundColor: '#3b82f6' }} viewport={{ once: true }} transition={{ delay: 0.2 }} />
                                
                                <CardContainer
                                    initial={{ opacity: 0, x: item.isLeft ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    whileHover="hover"
                                    variants={{ hover: { scale: 1.02, rotateX: 2, rotateY: item.isLeft ? 2 : -2 } }}
                                >
                                    <StarStreak style={{ top: 0, left: '-100%' }} variants={{ hover: { left: '100%', transition: starTransition } }} />
                                    <StarStreak vertical style={{ right: 0, top: '-100%' }} variants={{ hover: { top: '100%', transition: { ...starTransition, delay: 0.6 } } }} />
                                    <StarStreak style={{ bottom: 0, right: '-100%' }} variants={{ hover: { right: '100%', transition: { ...starTransition, delay: 1.2 } } }} />
                                    <StarStreak vertical style={{ left: 0, bottom: '-100%' }} variants={{ hover: { bottom: '100%', transition: { ...starTransition, delay: 1.8 } } }} />

                                    <InnerCard>
                                        <Typography sx={{ color: '#3b82f6', fontWeight: 900, fontSize: '0.8rem', mb: 1.5 }}>
                                            {item.year}
                                        </Typography>

                                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#fff', fontSize: '1.6rem' }}>
                                            {item.role}
                                        </Typography>
                                        <Typography sx={{ color: '#94a3b8', mb: 3, fontWeight: 600, letterSpacing: 1 }}>
                                            {item.company?.toUpperCase()}
                                        </Typography>
                                        
                                        <Typography sx={{ color: '#cbd5e1', mb: 4, lineHeight: 1.7, fontSize: '0.95rem' }}>
                                            {item.desc}
                                        </Typography>
                                        
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {skillsArray.map((skill, i) => (
                                                <Box key={i} sx={{ 
                                                    px: 1.5, py: 0.5, borderRadius: '6px', fontSize: '0.7rem',
                                                    background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa',
                                                    border: '1px solid rgba(59, 130, 246, 0.2)', fontWeight: 700
                                                }}>
                                                    {skill}
                                                </Box>
                                            ))}
                                        </Stack>
                                    </InnerCard>
                                </CardContainer>
                            </Box>
                        );
                    })}
                </Box>
            </Container>
        </JourneyWrapper>
    );
};

export default CareerJourney;
